<script lang="ts" setup>
import PreviewProjectModel from '@/models/PreviewProjectModel'
import appStore from '@/store/AppStore'
import userStore from '@/store/UserStore'
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import { BusEvent, StringMap } from '@/helper/Types'
import { onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { api } from '@/services/ApiService'
import ProjectController from '@/controllers/ProjectController'
import { ProjectOverviewResponse, ProjectResponse } from '@/services/models/ProjectOverviewResponse'
import ProjectListItem from '@/components/projects/ProjectListItem.vue'
import { hj } from '@/helper/analytics'
import { useRouter } from 'vue-router'

const COMPONENT_NAME = 'ProjectList'

const reversedOrder = ref(true)
const sortColumn = ref('')
const projectList = ref<PreviewProjectModel[]>([])
const totalProjects = ref(0)
const currentProjectPage = ref(1)
const projectPageCount = ref(15)
const router = useRouter()

const props = defineProps<{
  filterValue: string
}>()

userStore
  .loadProjects(
    currentProjectPage.value,
    projectPageCount.value,
    appStore.projectSorting.column,
    appStore.projectSorting.reversed
  )
  .then(res => addProjects(res))
  .catch(e => console.error(e.message()))

sortColumn.value = appStore.projectSorting.column
reversedOrder.value = appStore.projectSorting.reversed

bus.$onC(COMPONENT_NAME, BusEvent.DELETE_CONFIRMED, confirmDelete)
bus.$onC(COMPONENT_NAME, BusEvent.COPY_CONFIRMED, confirmCopy)
bus.$onC(COMPONENT_NAME, BusEvent.UPDATE_PROJECT_CONFIRMED, updateProject)

watch(() => props.filterValue, reloadProjectList)

onMounted(async () => {
  const projectId = window.history.state.updateProject?.toString()
  if (projectId) {
    await openUpdateProductsModal(projectId)
  }
})

async function loadMoreProjectsClicked(): Promise<void> {
  currentProjectPage.value += 1
  userStore
    .loadProjects(
      currentProjectPage.value,
      projectPageCount.value,
      appStore.projectSorting.column,
      appStore.projectSorting.reversed,
      props.filterValue
    )
    .then(res => addProjects(res))
    .catch(e => console.error(e.message()))
}

function addProjects(response: ProjectOverviewResponse | undefined, reset = false) {
  if (!response) {
    return
  }

  if (reset) {
    projectList.value = []
  }
  totalProjects.value = response.total

  for (const project of response.results) {
    projectList.value.push(createPreviewProjectModel(project))
  }
}

function createPreviewProjectModel(projectResponse: ProjectResponse): PreviewProjectModel {
  return {
    id: projectResponse.id,
    name: projectResponse.projectName,
    customerName:
      (projectResponse.customerData?.firstName || '') + ' ' + (projectResponse.customerData?.lastName || ''),
    lastModified: new Date(projectResponse.lastModified),
    status: projectResponse.status,
    totalPrice: projectResponse.totalPrice,
    debugArchiveUrl: projectResponse.debugArchiveUrl,
  }
}

function sort(sortColumnC: string, defaultReverseOrder: boolean, e: MouseEvent): void {
  hj('projects_sort_by_' + sortColumnC)

  if (e.screenX !== 0 && e.screenY !== 0) {
    (e.currentTarget as HTMLInputElement).blur()
  }
  reversedOrder.value = sortColumn.value === sortColumnC ? !reversedOrder.value : defaultReverseOrder
  sortColumn.value = sortColumnC
  appStore.projectSorting = { column: sortColumn.value, reversed: reversedOrder.value }
  reloadProjectList()
}

async function openUpdateProductsModal(projectId: string): Promise<void> {
  const updateProducts = await ProjectController.getProjectUpdateProducts(projectId)
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.UPDATE_PROJECT,
    confirmEventName: BusEvent.UPDATE_PROJECT_CONFIRMED,
    payload: reactive({ updateProducts, updateConfirmed: false, projectId })
  })
}

async function updateProject(payload: StringMap): Promise<void> {
  appStore.isLoading = true
  try {
    if (payload.updateProducts.priceUpdate && payload.updateProducts.priceUpdate.length) {
      await api.updateProjectPrices(payload.projectId)
    }

    await router.push({
      name: 'project',
      params: {
        projectId: payload.projectId
      },
      state: {
        phasedOutReview: payload.updateProducts.phasedOut.length ? 1 : 0
      }
    })
  } finally {
    appStore.isLoading = false
  }
}

async function confirmDelete(id: string): Promise<void> {
  hj('projects_delete_confirm')

  await ProjectController.deleteProject(id)
  await reloadProjectList()
}

async function confirmCopy(payload: StringMap): Promise<void> {
  hj('projects_copy_confirm')

  await ProjectController.copyProject(payload.projectId, payload.copiedName)
  await reloadProjectList()
}

async function reloadProjectList(): Promise<void> {
  currentProjectPage.value = 1
  const projectsResults = await userStore.loadProjects(
    currentProjectPage.value,
    projectPageCount.value,
    appStore.projectSorting.column,
    appStore.projectSorting.reversed,
    props.filterValue
  )

  projectList.value = []
  addProjects(projectsResults)
}

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})
</script>

<template>
  <div class="projects">
    <div v-if="totalProjects > 0" class="fTable is--clickable is--sortable card">
      <div class="fTable__tr has--th">
        <button
          :class="{
            'is--sorted': sortColumn === 'name',
            'is--reversed': reversedOrder === true && sortColumn === 'name'
          }"
          class="fTable__th column is--name btn-plain"
          tabindex="3"
          @click="sort('name', false, $event)"
        >
          <span>{{ $t('overview.table_project_name') }}</span>
        </button>
        <button
          :class="{
            'is--sorted': sortColumn === 'customer',
            'is--reversed': sortColumn === 'customer' ? reversedOrder : true
          }"
          class="fTable__th column is--customer btn-plain"
          tabindex="4"
          @click="sort('customer', false, $event)"
        >
          <span>{{ $t('overview.table_customer') }}</span>
        </button>
        <button
          :class="{
            'is--sorted': sortColumn === 'lastModified',
            'is--reversed': sortColumn === 'lastModified' ? reversedOrder : true
          }"
          class="fTable__th column is--last-edited btn-plain"
          tabindex="5"
          @click="sort('lastModified', true, $event)"
        >
          <span>{{ $t('overview.table_last_edited') }}</span>
        </button>
        <div
          v-if="!userStore.hidePrices"
          :title=" $t('overview.legal_price_note')"
          class="fTable__th column is--price btn-plain not--sortable"
          tabindex="6"
        >
          <span>
            {{ $t('overview.table_total_price') }}
            <a class="bottom-note-anchor" href="#foot-note">*</a>
          </span>
        </div>
        <button
          class="fTable__th column is--status btn-plain not--sortable"
          tabindex="7"
        >
          <span>{{ $t('overview.table_status') }}</span>
        </button>
        <div class="fTable__th column is--actions btn-spacer" />
      </div>

      <ProjectListItem
        v-for="(project, i) in projectList"
        :key="i"
        :hide-prices="userStore.hidePrices"
        :project="project"
      />

      <div v-if="projectList.length < totalProjects" class="fTable__tr is--footer">
        <button class="btn is--tertiary" @click="loadMoreProjectsClicked">
          {{ $t('overview.load_more_projects') }}
        </button>
      </div>
    </div>
    <div v-if="totalProjects" class="foot-note">
      <div id="foot-note">
        * {{ $t('overview.legal_price_note') }}
      </div>
      <div class="projects__count">
        {{ $t('overview.projects_pagination_index', { num: projectList.length, total: totalProjects }) }}
      </div>
    </div>

    <div v-if="totalProjects === 0" class="empty-results">
      <div class="empty-results__icon">
        <i class="ic-search" />
      </div>
      <span class="h1 empty-results__title">{{ $t('global.no_results_title') }}</span>
      <span class="empty-results__text">{{ $t('global.no_results_description') }}</span>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';
@import '@/assets/style/helpers/variables';

.projects {
  margin-bottom: rem-calc(16);

  .foot-note {
    .projects__count {
      color: $textColor;
      flex-basis: 50%;
      margin-left: 1rem;
    }
  }

  .fTable {
    overflow: visible;

    @include media('<tablet') {
      .is--customer,
      .is--last-edited,
      .is--price {
        display: none;
      }
    }

    &__tr {
      &:last-child {
        @include themify($themes) {
          border-bottom-left-radius: rem-calc(themed('cardBorderRadius'));
          border-bottom-right-radius: rem-calc(themed('cardBorderRadius'));
        }
      }

      &:first-child {
        @include themify($themes) {
          border-top-left-radius: rem-calc(themed('cardBorderRadius'));
          border-top-right-radius: rem-calc(themed('cardBorderRadius'));
        }
      }

      &.is--footer {
        justify-content: center;
        padding: rem-calc(24);
      }
    }

    &__td {
      min-height: rem-calc(73);

      @include media('>=desktop') {
        min-height: auto;
      }
    }
  }

  .fTable__th.btn-plain {
    .has--outline &:focus {
      @include focusStyleInset();
    }
  }

  .fTable {
    .column {
      align-items: center;

      &.is--name {
        flex-basis: 100%;
        flex-grow: 1;
        flex-shrink: 1;
        min-width: rem-calc(200px);
        font-weight: bold;

        @include media('>=desktop') {
          font-weight: normal;
        }

        span {
          font-weight: normal;
          display: block;
        }

        &.fTable__td {
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
        }

        &.fTable__th {
          flex-direction: row;
          align-items: center;
        }
      }

      &.is--last-edited {
        min-width: 19.8%;
        flex-basis: 19.8%;
        flex-grow: 0;
        flex-shrink: 1;
      }

      &.is--actions {
        overflow: visible;
        align-items: center;
        flex-basis: rem-calc(94);
        flex-shrink: 0;
        flex-grow: 0;

        &.fTable__th {
          cursor: default;

          &:before {
            content: none;
          }
        }

        .icon-btn {
          padding: 0 rem-calc(12);
          box-sizing: content-box;
          width: rem-calc(30);
          height: rem-calc(30);

          &:last-child {
            margin-right: 0;
          }
        }

        & > span {
          display: inline-flex;
        }
      }

      &.is--status {
        align-items: center;
      }
    }
  }

  &__count {
    text-align: right;
    font-size: rem-calc(14);
  }
}

.empty-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: rem-calc(60) auto;

  &__icon {
    width: rem-calc(300);
    margin-bottom: rem-calc(32);
    max-width: 100%;
    height: rem-calc(300);
    background: $tableHoverColor;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;

    i[class^='ic-'],
    i[class*=' ic-'] {
      font-size: rem-calc(160);
      color: $placeholderColor;
    }
  }

  &__title {
    margin-bottom: rem-calc(16) !important;
  }
}
</style>
